import { PurchasableProduct } from '@accedo/one-plugin-sdk';
import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import { useNavigation } from '#/hooks/useNavigation';
import { collectProductDetails } from '#/pages/Settings/Products.utils';
import { useAuthStore } from '#/store/auth/auth.store';
import {
  getPurchasableProducts,
  paywallActions,
  usePaywallStore,
} from '#/store/paywall/paywall.store';
import { determinePurchaseOrDirection } from '#/store/paywall/paywall.utils';

import {
  Actions,
  PurchaseFlowStep,
  PurchaseFlowStore,
} from './PlanPurchaseFlow.types';

interface Props {
  actions: Actions;
}

// Beware that the steps starts on 2, not 1. This is because the first step is the registration step.
export const stepInformation = {
  [PurchaseFlowStep.PICK]: {
    title: 'iap.selectYourPlan',
    subtitle: 'iap.selectYourPlanSubtitle',
    stepNumber: 2,
  },
  [PurchaseFlowStep.PURCHASE]: {
    title: 'iap.setUpYourPayment',
    subtitle: 'iap.setUpYourPaymentSubtitle',
    stepNumber: 3,
  },
};

export const usePlanPurchaseFlow = ({ actions }: Props) => {
  const { goHome } = useNavigation('PlanPurchaseFlowV2');
  const {
    tempSession: { productsResponse, currentPaywallParams },
    paywallApi,
  } = usePaywallStore();

  const { profileResponse } = useAuthStore();
  const [store, setStore] = useState<PurchaseFlowStore>({
    hasPaymentMethod: false,
  });
  const [currentStep, setCurrentStep] = useState<PurchaseFlowStep>(
    PurchaseFlowStep.PICK
  );

  const packageDefinition = productsResponse?.packageDefinition;

  // fetch purchasable products
  const {
    data: { purchasableProducts, otherPlatformError } = {},
    error: purchasableProductsError,
    isLoading: isPurchasableProductsLoading,
  } = useSWR(
    !!getPurchasableProducts && ['purchasable-products', currentPaywallParams],
    () => getPurchasableProducts(currentPaywallParams),
    {
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: console.error,
    }
  );

  // fetch current payment method, error means there is none
  const { isLoading: isPaymentMethodLoading } = useSWR(
    'payment-method',
    () => paywallApi.getPaymentDetails?.(),
    {
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      errorRetryCount: 0,
      onSuccess: (paymentMethod) => {
        setStore((prev) => ({ ...prev, hasPaymentMethod: !!paymentMethod }));
      },
    }
  );

  const { userProductIds } = useMemo(
    () => collectProductDetails(profileResponse),
    [profileResponse]
  );

  const handleProductPicked = useCallback(
    (selectedProduct: PurchasableProduct) => {
      const direction = determinePurchaseOrDirection(
        packageDefinition!,
        userProductIds,
        selectedProduct.id,
        paywallActions.transformProductIdentifierSafely
      );

      if (direction.action === 'PURCHASE') {
        setCurrentStep(PurchaseFlowStep.PURCHASE);
        setStore((prevStore) => ({ ...prevStore, selectedProduct }));
      } else {
        setCurrentStep(PurchaseFlowStep.CHANGE_ADD_PAYMENT_METHOD);
        setStore((prevStore) => ({ ...prevStore, selectedProduct, direction }));
      }
    },
    [packageDefinition, userProductIds]
  );

  const handlePaymentMethodAdded = useCallback(() => {
    setStore((prev) => ({ ...prev, hasPaymentMethod: true }));
    setCurrentStep(PurchaseFlowStep.CHANGE_CONFIRM);
  }, []);

  const handlePurchaseCancel = useCallback(() => {
    setCurrentStep(PurchaseFlowStep.PICK);
  }, []);

  const handleChangeCancel = useCallback(() => {
    setCurrentStep(PurchaseFlowStep.PICK);
  }, []);

  const handleCloseAndGoHome = useCallback(() => {
    actions.handleClose();
    goHome();
  }, [actions, goHome]);

  return {
    store,
    currentStep,
    profileResponse,
    handleProductPicked,
    handleCloseAndGoHome,
    handlePaymentMethodAdded,
    handlePurchaseCancel,
    handleChangeCancel,
    packageDefinition,
    purchasableProducts,
    isPurchasableProductsLoading,
    otherPlatformError,
    purchasableProductsError,
    isPaymentMethodLoading,
  };
};
